// Right-sized catalogue images through Cloudflare image transformations
// (speed Phase 1, 2026-10-09). See ops/2026-10-08-phase1-image-cdn-prep.md.
//
// OFF by default, and while it is off every function here returns exactly
// what it was given: merging this changes no URL on the site. It is turned
// on only once images.emporia.ke exists as an R2 custom domain with
// transformations enabled — before that, every rewritten URL would 404.
//
// Two rules keep the Cloudflare bill at $0 and must not be relaxed:
//  * ONLY widths on LADDER. Cloudflare bills each (image, size) pair once a
//    month, so a width computed from the screen would make every device a
//    new billable transformation. The 5,000 free ones cover the catalogue
//    up to ~430 products at three sizes each.
//  * onerror=redirect on every URL, so if the free allowance ever runs out
//    a shopper gets the original image, not a broken one (Cloudflare
//    answers a new size with error 9422 once it is exhausted).
//
// format=auto serves AVIF to browsers that accept it, then WebP, then the
// original format — and it counts as ONE transformation, not one per format.

// ON since 2026-10-13: images.emporia.ke is connected to the R2 bucket and
// transformations are enabled for the zone (owner, verified: AVIF 66 KB for
// the 1.8 MB hero, foreign origins refused 403).
export const IMAGE_CDN_ENABLED = true;
export const IMAGE_CDN_ORIGIN = "https://images.emporia.ke";
const QUALITY = 75;

// Where our catalogue images live. Anything else (Netlify-hosted category
// art, a pasted external URL, a data: or blob: preview) passes through.
const R2_PUBLIC_RE = /^https:\/\/pub-[a-z0-9]+\.r2\.dev\/(.+)$/;
const CDN_RE = /^https:\/\/images\.emporia\.ke\/(?!cdn-cgi\/)(.+)$/;

export const LADDER = Object.freeze({
  product: [480, 960, 1600],
  hero: [960, 1600, 2400],
  tile: [240, 480],
});
const ALLOWED = new Set(Object.values(LADDER).flat());

let enabled = IMAGE_CDN_ENABLED;
/** Test seam. Production code never calls this. */
export const _setImageCdn = (on) => { enabled = Boolean(on); };

/** The object key of a catalogue image, or null if it is not one of ours. */
export function r2Key(src) {
  if (typeof src !== "string") return null;
  const m = src.match(R2_PUBLIC_RE) || src.match(CDN_RE);
  if (!m) return null;
  // Keys are plain paths (products/<uuid>.png). Refuse anything that could
  // smuggle options into the transformation URL.
  return /^[A-Za-z0-9._\-/]+$/.test(m[1]) ? m[1] : null;
}

/** One image at one ladder width; `src` unchanged when off or not ours. */
export function imageUrl(src, width) {
  if (!enabled) return src;
  if (!ALLOWED.has(width)) throw new Error(`imageUrl: ${width} is not on the ladder`);
  const key = r2Key(src);
  if (!key) return src;
  return `${IMAGE_CDN_ORIGIN}/cdn-cgi/image/width=${width},format=auto,quality=${QUALITY},onerror=redirect/${key}`;
}

/** A srcset for one kind of image, or undefined (attribute omitted). */
export function imageSrcSet(src, kind) {
  if (!enabled || !r2Key(src)) return undefined;
  return LADDER[kind].map((w) => `${imageUrl(src, w)} ${w}w`).join(", ");
}

/**
 * Everything an <img> needs: spread it in place of `src`.
 *   <img {...imageProps(product.image_url, "product", SIZES.card)} alt=… />
 * When off: exactly { src }, so the rendered markup is unchanged.
 */
export function imageProps(src, kind, sizes) {
  const srcSet = imageSrcSet(src, kind);
  if (!srcSet) return { src };
  const ladder = LADDER[kind];
  // The plain src is only for browsers without srcset: the middle rung.
  return { src: imageUrl(src, ladder[Math.min(1, ladder.length - 1)]), srcSet, sizes };
}

// `sizes` for each place an image renders: roughly how wide it is drawn, so
// the browser picks the smallest rung that is still sharp.
export const SIZES = Object.freeze({
  card: "(max-width: 700px) 50vw, 300px",
  thumb: "120px",
  mosaic: "200px",
  gallery: "(max-width: 900px) 100vw, 50vw",
  tile: "160px",
  // The hero is object-fit: cover in a box about 500px tall at a 2:1
  // aspect, so on a phone it is drawn ~1000px wide whatever the viewport.
  hero: "(max-width: 900px) 1000px, 100vw",
});

/**
 * Start (or reuse) the download an <img> with these props would make.
 * A detached Image given the same srcset and sizes picks the same candidate
 * as the real <img>, so a warm-up never fetches a different URL — which,
 * with the CDN on, would download every hero twice.
 */
export function warmImage(src, kind, sizes) {
  return new Promise((resolve) => {
    if (!src) { resolve(); return; }
    const props = imageProps(src, kind, sizes);
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    if (props.srcSet) { img.sizes = props.sizes; img.srcset = props.srcSet; }
    img.src = props.src;
  });
}
